import { logEvent } from './debugLog';

/**
 * Embed bridge — lets partner apps (events, shopping, …) host NavMe in a
 * WebView/iframe as their venue "experience layer".
 *
 * URL params (in addition to ?dest= / ?tour= handled in main):
 *   ?embed=1        partner chrome: hides Setup/Dashboard buttons on landing
 *   ?accent=RRGGBB  brand accent color (hex, no #)
 *   ?stream=1       emit 1 Hz `position` events (map space) to the host
 *
 * Outbound (window.parent.postMessage, {source:'navme', type, ...}):
 *   ready | localized | route_start {poiId} | arrived {poiId} |
 *   tour_complete {tourId} | position {x,y,z} | closed
 * Inbound (host → iframe, {target:'navme', type, ...}):
 *   navigate {poiId} | setLang {lang}
 *
 * Security: outbound uses targetOrigin '*' because the payloads contain no
 * secrets (positions and public POI ids only). Inbound commands are limited
 * to navigation/UI verbs — nothing destructive is exposed.
 */

const params = new URLSearchParams(location.search);

export function isEmbedded(): boolean {
  return params.get('embed') === '1';
}

export function wantsPositionStream(): boolean {
  return params.get('stream') === '1';
}

/** Applies ?accent=RRGGBB branding. Call once at boot. */
export function applyEmbedTheme(): void {
  const accent = params.get('accent');
  if (accent && /^[0-9a-fA-F]{6}$/.test(accent)) {
    document.documentElement.style.setProperty('--accent', `#${accent}`);
    logEvent('session', `embed accent applied #${accent}`);
  }
}

export function emitEmbed(type: string, payload: Record<string, unknown> = {}): void {
  if (window.parent === window) return; // not embedded — no-op
  try {
    window.parent.postMessage({ source: 'navme', type, ...payload }, '*');
  } catch {
    /* never break the app for the bridge */
  }
}

export interface EmbedCommandHandlers {
  navigate: (poiId: string) => void;
  setLang: (lang: string) => void;
}

/** Listens for host-app commands. Call once at boot. */
export function initEmbedCommands(handlers: EmbedCommandHandlers): void {
  window.addEventListener('message', (ev: MessageEvent) => {
    const d = ev.data as { target?: string; type?: string; poiId?: string; lang?: string } | null;
    if (!d || d.target !== 'navme') return;
    logEvent('session', `embed command: ${d.type}`);
    if (d.type === 'navigate' && typeof d.poiId === 'string') handlers.navigate(d.poiId);
    else if (d.type === 'setLang' && typeof d.lang === 'string') handlers.setLang(d.lang);
  });
}
